import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const TaskItemContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isHighlighted',
})<{ isHighlighted?: boolean }>(({ theme, isHighlighted }) => ({
  borderRadius: '0.75rem',
  backgroundColor: '#f5f5f7',
  padding: '0.25rem 0.5rem',
  ...(isHighlighted && {
    border: `2px solid ${theme.palette.accent.main}`,
    padding: '0.125rem 0.375rem',
  }),
}));

export const TaskHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasDetails',
})<{ hasDetails?: boolean }>(({ theme, hasDetails }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  cursor: hasDetails ? 'pointer' : 'default',
  '&:hover': hasDetails
    ? {
        opacity: 0.8,
      }
    : {},
}));

export const TaskTitle = styled(Typography)(
  () => `
font-size: 1rem;
font-style: normal;
font-weight: 500;
line-height: normal;
color: #615e6f;
`,
);

export const TaskBadges = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

export const TaskDescription = styled(Typography)(
  ({ theme }) => `
font-size: 0.625rem;
font-style: normal;
font-weight: 400;
line-height: 160%;
padding-left: 1.5rem;
margin: 0.68rem 0;
color: ${theme.palette.grey[800]};
`,
);

export const TaskMeta = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.6rem',
  flexWrap: 'wrap',
  paddingLeft: '1.5rem',
}));

export const MetaItem = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.25rem',
}));

export const MetaIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& svg': {
    width: '1rem',
    height: '1rem',
    color: theme.palette.grey[800],
  },
}));

export const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: '160%',
  color: theme.palette.grey[800],
}));

export const ProgressContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.75rem',
  width: '100%',
}));

export const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  flex: 1,
  height: '6px',
  borderRadius: '3px',
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.purple?.[600] || '#9333EA',
    borderRadius: '3px',
  },
}));

export const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.grey[600],
  fontWeight: 500,
  minWidth: '40px',
  textAlign: 'right',
}));

export const ResolveButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  textTransform: 'none',
  padding: 0,
  minWidth: 0,
  gap: 0,

  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
  '& .MuiButton-endIcon': {
    marginLeft: '2px',
  },
}));

export const Priority = styled('span', { shouldForwardProp: (prop) => prop !== 'priority' })<{
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
}>(({ priority, theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  borderRadius: '0.4375rem',
  padding: '0 0.44rem',
  backgroundColor:
    priority === 'HIGH'
      ? theme.palette.red[100]
      : priority === 'MEDIUM'
        ? theme.palette.orange[100]
        : '#E0E7FF',
  color:
    priority === 'HIGH'
      ? theme.palette.red[700]
      : priority === 'MEDIUM'
        ? theme.palette.orange[700]
        : '#4F46E5',
  textTransform: 'uppercase',
}));

export const StatusBadge = styled('div')<{
  statustype: string;
}>(({ statustype, theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  borderRadius: '0.4375rem',
  padding: '0 0.44rem',
  textTransform: 'uppercase',

  ...(statustype === 'NEW' && {
    backgroundColor: '#dfdbf9',
    color: '#7f52d5',
  }),
  ...(statustype === 'IN_PROGRESS' && {
    backgroundColor: theme.palette.blue[100],
    color: theme.palette.blue[700],
  }),
  ...(statustype === 'ON_HOLD' && {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[800],
    borderColor: theme.palette.grey[800],
  }),
  ...(statustype === 'COMPLETED' && {
    backgroundColor: theme.palette.green[100],
    color: theme.palette.green[700],
  }),
  ...(statustype === 'CANCELLED' && {
    backgroundColor: theme.palette.red[100],
    color: theme.palette.red[700],
  }),
}));
